<script lang="ts">
    import { Video, Clock, AlertCircle, Share } from 'lucide-svelte';
    import { format } from 'date-fns';
    import { ja } from 'date-fns/locale';
    import { page } from '$app/stores';
    import { goto } from '$app/navigation';
    import { DateTimeUtils } from '$lib/utils/dateTimeUtils';
    import { formatDuration } from '$lib/utils/formatUtils';
    import { onMount } from 'svelte';
    import { getMaxConcurrentViewers } from '$lib/utils/statsUtils';
    import ContentCard from '$lib/components/ContentCard.svelte';
    import Carousel from '$lib/components/Carousel.svelte';

    export let data;
    $: ({ groupId, groupInfo, groupChannels, latestVideos, liveStreams, upcomingChannels, groups } = data);

    // 明示的な型定義を追加
    interface Channel {
        id: string;
        scheduled_start_time: string;
        channel_title: string;
        channel_id?: string;
        title?: string;
        thumbnail_url?: string;
        timeOnly?: string;
        channelName?: string;
    }

    interface Video {
        id: string;
        published_at: string;
        title: string;
        thumbnail_url: string;
        channel_title: string;
        channel_id: string;
        duration?: string;
        live_stats?: Record<string, number>;
    }

    let elapsedTimes: Record<string, string> = {};
    let selectedGroup = groupInfo?.id || '';
    let activeTab = 'live';
    let selectedDate = '';
    let selectedTimeRange = 'all';
    let upcomingHeaderEl: HTMLElement;    
    // 日付フィルター用の日付リスト
    $: availableDates = [...new Set(upcomingChannels.map(channel => formatDate(channel.scheduled_start_time)))].sort();
    
    // 時間帯フィルター
    const timeRanges = [
        {id: 'all', label: 'すべて'},
        {id: 'morning', label: '朝 (5:00-12:00)'},
        {id: 'afternoon', label: '昼 (12:00-18:00)'},
        {id: 'evening', label: '夜 (18:00-5:00)'}
    ];

    function formatScheduledStartTime(dateString: string): string {
        const date = new Date(dateString);
        return format(date, 'M月d日(E) HH:mm', { locale: ja });
    }

    function formatDate(dateString: string): string {
        const date = new Date(dateString);
        return format(date, 'yyyy年M月d日', { locale: ja });
    }

    function setActiveTab(tab: string) {
        activeTab = tab;
        goto(`?tab=${tab}`, { replaceState: true });
    }

    function groupVideosByDate(videos: Video[]) {
        return videos.reduce((groups: Record<string, Video[]>, video: Video) => {
            const date = formatDate(video.published_at);
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(video);
            return groups;
        }, {});
    }

    // 時間帯を定義
    function getTimeRange(hour: number): string {
        if (hour >= 5 && hour < 12) return '朝 (5:00-12:00)';
        if (hour >= 12 && hour < 18) return '昼 (12:00-18:00)';
        if (hour >= 18 || hour < 5) return '夜 (18:00-5:00)';
        return '';
    }
    
    // 日付で配信予定をフィルタリング
    $: filteredUpcomingChannels = upcomingChannels.filter(channel => {
        // 日付フィルターがあれば適用
        if (selectedDate && formatDate(channel.scheduled_start_time) !== selectedDate) {
            return false;
        }
        
        // 時間帯フィルターがあれば適用
        if (selectedTimeRange !== 'all') {
            const date = new Date(channel.scheduled_start_time);
            const hour = date.getHours();
            const timeRange = getTimeRange(hour);
            
            if (selectedTimeRange === 'morning' && timeRange !== '朝 (5:00-12:00)') return false;
            if (selectedTimeRange === 'afternoon' && timeRange !== '昼 (12:00-18:00)') return false;
            if (selectedTimeRange === 'evening' && timeRange !== '夜 (18:00-5:00)') return false;
        }
        
        return true;
    });

    function groupVideosByDateAndTimeRange(channels: Channel[]) {
        return channels.reduce((groups: Record<string, Record<string, Channel[]>>, channel: Channel) => {
            const date = formatDate(channel.scheduled_start_time);
            const startTime = new Date(channel.scheduled_start_time);
            const hour = startTime.getHours();
            const timeRange = getTimeRange(hour);
            
            // タイムゾーンを考慮した時間表示に修正
            const formattedTime = DateTimeUtils.formatScheduledStartTime(channel.scheduled_start_time);
            const timeOnly = formattedTime.split(' ')[1]; // "5月20日(月) 21:00" から "21:00" だけ取得

            if (!groups[date]) {
                groups[date] = {};
            }
            if (!groups[date][timeRange]) {
                groups[date][timeRange] = [];
            }

            // 時刻情報を含めてチャンネル情報を保存
            groups[date][timeRange].push({
                ...channel,
                timeOnly
            });

            return groups;
        }, {});
    }

    function updateElapsedTimes() {
        liveStreams.forEach((stream: { id: string; scheduled_start_time?: string }) => {
            if (!stream.scheduled_start_time) {
                elapsedTimes = { ...elapsedTimes, [stream.id]: 'LIVE' };
                return;
            }
            const startTime = DateTimeUtils.convertToISODate(stream.scheduled_start_time);
            if (isNaN(startTime.getTime())) {
                return;
            }
            const now = new Date();
            const seconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);
            elapsedTimes = { ...elapsedTimes, [stream.id]: DateTimeUtils.formatTimeToHHMMSS(seconds) };
        });
    }
    
    // スクロールイベントハンドラー
    function handleScroll() {
        if (!upcomingHeaderEl) return;
        
        const headerRect = upcomingHeaderEl.getBoundingClientRect();
    }

    onMount(() => {
        // @ts-ignore
        document.querySelectorAll('.adsbygoogle').forEach(ad => {
            try {
                // @ts-ignore
                (window.adsbygoogle = window.adsbygoogle || []).push({});
            } catch (e) {
            }
        });
        // 経過時間の更新を開始
        updateElapsedTimes();
        const interval = setInterval(() => {
            updateElapsedTimes();
        }, 1000);
        
        // スクロールイベントリスナーを追加
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            clearInterval(interval);
            window.removeEventListener('scroll', handleScroll);
        };
    });

    async function handleGroupChange(newGroupId: string) {
        selectedGroup = newGroupId;
        await goto(`/group/${newGroupId}`, { 
            invalidateAll: true  // データを再取得
        });
    }

    // 現在選択中のグループの配信予定データに基づいたシェアテキストを作成
    $: shareText = (() => {
        const today = format(new Date(), 'yyyy/MM/dd', { locale: ja });
        const groupName = groupInfo?.name || '';
        
        // 今日の配信予定をすべて取得
        const todayStreams = (upcomingChannels || []).filter((channel: Channel) => {
            const streamDate = format(new Date(channel.scheduled_start_time), 'yyyy/MM/dd');
            const todayDate = format(new Date(), 'yyyy/MM/dd');
            return streamDate === todayDate;
        });
        
        // 時間帯ごとにグループ化
        const streamsByTimeRange: Record<string, Channel[]> = {};
        
        todayStreams.forEach((channel: Channel) => {
            const date = new Date(channel.scheduled_start_time);
            const hour = date.getHours();
            let timeRange = '';
            
            if (hour >= 5 && hour < 12) timeRange = '🌅 朝 (5:00-12:00)';
            else if (hour >= 12 && hour < 18) timeRange = '☀️ 昼 (12:00-18:00)';
            else timeRange = '🌙 夜 (18:00-5:00)';
            
            if (!streamsByTimeRange[timeRange]) {
                streamsByTimeRange[timeRange] = [];
            }
            
            streamsByTimeRange[timeRange].push(channel);
        });
        
        // 時間帯ごとにテキストを作成（コンパクトに）
        let formattedText = '';
        Object.entries(streamsByTimeRange).forEach(([timeRange, channels]) => {
            if (channels.length > 0) {
                // 時間帯のヘッダーを追加
                formattedText += `\n${timeRange}\n`;
                
                // その時間帯の配信をソートして追加
                const sortedChannels = channels.sort((a, b) => {
                    const timeA = new Date(a.scheduled_start_time).getTime();
                    const timeB = new Date(b.scheduled_start_time).getTime();
                    return timeA - timeB;
                });
                
                sortedChannels.forEach((channel: Channel) => {
                    // ここで時間を明示的に表示フォーマットする
                    const startTime = new Date(channel.scheduled_start_time);
                    const formattedTime = format(startTime, 'HH:mm', { locale: ja });
                    formattedText += `${formattedTime} ${channel.channel_title}\n`;
                });
            }
        });
        
        // URLエンコードして返す
        return encodeURIComponent(`${today} ${groupName}の今日の配信予定\n${formattedText}\n#VTuber #配信予定`);
    })();

    // 時間帯ごとにソート
    $: sortedTimeRangeChannels = Object.entries(groupVideosByDateAndTimeRange(filteredUpcomingChannels))
        .map(([date, timeGroups]) => ({
            date,
            timeGroups: Object.entries(timeGroups)
                .map(([timeRange, channels]) => ({
                    timeRange,
                    channels: [...channels].sort((a, b) => {
                        // timeOnlyが両方存在する場合のみ比較を行う
                        if (a.timeOnly && b.timeOnly) {
                            return a.timeOnly.localeCompare(b.timeOnly);
                        }
                        // 片方のみが存在する場合
                        if (a.timeOnly) return -1;
                        if (b.timeOnly) return 1;
                        // 両方存在しない場合
                        return 0;
                    })
                }))
                .sort((a, b) => Number(a.timeRange) - Number(b.timeRange))
        }))
        .sort((a, b) => Number(a.date) - Number(b.date));
</script>

<svelte:head>
    <title>{groupInfo?.name || 'グループ'} の配信とアーカイブ | VTGrid</title>
    <meta name="description" content="{groupInfo?.name || 'グループ'}の配信中・配信予定と過去のアーカイブ" />
    <meta name="keywords" content="{groupInfo?.name || 'グループ'}, 配信中, 配信予定, アーカイブ" />
    <meta property="og:title" content="{groupInfo?.name || 'グループ'}の配信中・配信予定と過去のアーカイブ | VTGrid" />
    <meta property="og:description" content="{groupInfo?.name || 'グループ'}の最新のライブ配信と過去のアーカイブ一覧" />
</svelte:head>

<!-- グループナビゲーション -->
<nav 
    class="border-b border-black/10 dark:border-white/10 sticky top-0 z-10"
    aria-label="グループ選択"
>
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
        <div class="h-12 relative">
            <div 
                class="absolute inset-0 flex items-center gap-3 overflow-x-auto no-scrollbar"
                role="tablist"
                aria-label="グループ一覧"
            >
                {#each groups as group (group.id)}
                    {@const isActive = parseInt(group.id) === groupId}
                    <button
                        on:click={() => handleGroupChange(group.id)}
                        class="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors shrink-0
                        {isActive ? 
                            'bg-black dark:bg-white text-white dark:text-black font-medium' : 
                            'text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-white/5'}"
                        aria-current={isActive ? 'page' : undefined}
                        role="tab"
                        aria-selected={isActive}
                    >
                        {#if group.icon_url}
                            <img 
                                src={group.icon_url} 
                                alt="" 
                                class="w-4 h-4 rounded-full ring-1 ring-black/10 dark:ring-white/10"
                                loading="lazy"
                                aria-hidden="true"
                            />
                        {/if}
                        <span>{group.name}</span>
                        {#if group.channelCount > 0}
                            <span class="text-xs opacity-70">({group.channelCount})</span>
                        {/if}
                    </button>
                {/each}
            </div>
        </div>
    </div>
</nav>

<!-- タブナビゲーション -->
<div class="sticky top-12 z-10 border-b border-black/10 dark:border-white/10">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
        <nav class="flex gap-6" aria-label="コンテンツタブ">
            <button
                class="relative py-3 transition-colors {activeTab === 'live' ? 'text-black dark:text-white' : 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white'}"
                on:click={() => setActiveTab('live')}
                aria-selected={activeTab === 'live'}
                role="tab"
                aria-controls="live-content"
                id="live-tab"
            >
                <span class="flex items-center gap-3 whitespace-nowrap text-sm">
                    <span class="font-medium flex items-center gap-1.5">
                        配信中 
                        <span class="inline-flex items-center px-1.5 rounded-full text-xs bg-black/90 dark:bg-white/90 text-white dark:text-black font-medium">
                            {liveStreams.length}
                        </span>
                    </span>
                    <span class="font-medium flex items-center gap-1.5">
                        配信予定
                        <span class="inline-flex items-center px-1.5 rounded-full text-xs bg-black/90 dark:bg-white/90 text-white dark:text-black font-medium">
                            {upcomingChannels.length}
                        </span>
                    </span>
                </span>
                {#if activeTab === 'live'}
                    <div class="absolute bottom-0 left-0 w-full h-0.5 bg-black dark:bg-white" />
                {/if}
            </button>

            <button
                class="relative py-4 transition-colors {activeTab === 'archived' ? 'text-black dark:text-white' : 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white'}"
                on:click={() => setActiveTab('archived')}
                aria-selected={activeTab === 'archived'}
                role="tab"
                aria-controls="archived-content"
                id="archived-tab"
            >
                <span class="flex items-center gap-2 whitespace-nowrap text-sm sm:text-base">
                    <span class="font-medium">アーカイブ</span>
                    {#if latestVideos.length > 0}
                        <span class="inline-flex items-center px-1.5 rounded-full text-xs bg-black/10 dark:bg-white/10 text-black/70 dark:text-white/70 font-medium">
                            {latestVideos.length}
                        </span>
                    {/if}
                </span>
                {#if activeTab === 'archived'}
                    <div class="absolute bottom-0 left-0 w-full h-0.5 bg-black dark:bg-white" />
                {/if}
            </button>
        </nav>
    </div>
</div>

<div class="min-h-screen text-gray-900 dark:text-white pt-2">
    {#if !groupInfo}
        <p role="alert">グループ情報の取得中...</p>
    {:else}
        <div class="mx-auto px-2 sm:px-6 lg:px-8 py-4">
            <div class="mb-4">
                <div class="flex flex-col sm:flex-row items-left justify-between">
                    <div class="flex items-center gap-3">
                        {#if groupInfo.icon_url}
                            <img 
                                src={groupInfo.icon_url} 
                                alt={`${groupInfo.name}のアイコン`}
                                class="w-6 h-6 rounded-full"
                                loading="lazy"
                            />
                        {/if}
                        <h1 class="text-lg font-semibold text-gray-900 dark:text-white">
                            {groupInfo.name} {activeTab === 'archived' ? 'の配信アーカイブ' : 'の配信予定'}
                        </h1>
                    </div>
                    
                    <!-- シェアボタン -->
                    {#if activeTab === 'live'}
                        <a 
                            href={`https://twitter.com/intent/tweet?text=${shareText}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-black/80 dark:text-white/80 rounded-full text-xs font-medium transition-colors mt-2 sm:mt-0"
                            aria-label="Xでシェア"
                        >
                            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
                            </svg>
                            <span>配信予定をシェア</span>
                        </a>
                    {/if}
                </div>
            </div>

            {#if !groupChannels.length}
                <p role="alert">このグループにはチャンネルがありません。</p>
            {:else}
                <!-- コンテンツ表示部分 -->
                {#if activeTab === 'live'}
                    <div 
                        role="tabpanel" 
                        id="live-content" 
                        aria-labelledby="live-tab"
                        class="pt-3"
                    >
                        <div class="flex items-center gap-2 mb-6">
                            <div class="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                            <h2 class="text-base font-medium text-black/90 dark:text-white/90">
                                配信中の動画
                            </h2>
                        </div>

                        {#if liveStreams.length === 0}
                            <div class="flex flex-col py-6 text-black/60 dark:text-white/60" role="status">
                                <p class="font-medium">現在配信中のチャンネルはありません</p>
                            </div>
                        {:else}
                            <!-- カルーセル表示に変更 -->
                            <div class="mb-4 relative group">
                                <div class="flex overflow-x-auto scrollbar-hide gap-2 pb-2">
                                    {#each liveStreams as stream}
                                        <div class="flex-shrink-0 w-36 sm:w-48 group">
                                            <div class="relative aspect-video rounded-lg overflow-hidden bg-black">
                                                <a href="/video/{stream.id}">
                                                    <img 
                                                        src={stream.thumbnail_url || `https://img.youtube.com/vi/${stream.id}/mqdefault.jpg`}
                                                        alt={stream.title}
                                                        class="w-full h-full object-cover"
                                                        style="aspect-ratio: 16/9; object-position: center;"
                                                        loading="lazy"
                                                    />
                                                </a>
                                                <div class="absolute top-1 right-1 px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-medium rounded flex items-center gap-1">
                                                    <span class="w-1 h-1 rounded-full bg-white animate-pulse"></span>
                                                    LIVE
                                                </div>
                                                {#if stream.live_stats}
                                                    {@const timestamps = Object.keys(stream.live_stats).sort()}
                                                    {@const currentViewers = timestamps.length > 0 ? stream.live_stats[timestamps[timestamps.length - 1]] : 0}
                                                    {#if currentViewers}
                                                        <div class="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 text-white text-[10px] font-medium rounded">
                                                            {currentViewers.toLocaleString()}
                                                        </div>
                                                    {/if}
                                                {/if}
                                                <div class="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/70 text-white text-[10px] font-medium rounded">
                                                    {elapsedTimes[stream.id] || '--:--:--'}
                                                </div>
                                            </div>
                                            <div class="mt-1.5">
                                                <div class="flex flex-col gap-1">
                                                    <a 
                                                        href="/video/{stream.id}" 
                                                        class="text-xs font-medium text-black dark:text-white line-clamp-2 leading-tight hover:underline"
                                                    >
                                                        {stream.title}
                                                    </a>
                                                    <a 
                                                        href="/channel/{stream.channel_id}" 
                                                        class="text-[10px] text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white transition-colors line-clamp-1"
                                                    >
                                                        {stream.channel_title}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    {/each}
                                </div>
                                
                                <!-- 左右のスクロールボタン -->
                                <button 
                                    class="absolute left-0 top-[25%] -translate-y-1/2 -translate-x-3 bg-white dark:bg-zinc-800 rounded-full p-1.5 shadow-md opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
                                    on:click={(e) => {
                                        const parent = e.currentTarget.parentElement;
                                        if (parent) {
                                            const scrollContainer = parent.querySelector('.overflow-x-auto');
                                            if (scrollContainer instanceof HTMLElement) {
                                                scrollContainer.scrollBy({ left: -300, behavior: 'smooth' });
                                            }
                                        }
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-black/70 dark:text-white/70">
                                        <path d="m15 18-6-6 6-6"/>
                                    </svg>
                                </button>
                                
                                <button 
                                    class="absolute right-0 top-[25%] -translate-y-1/2 translate-x-3 bg-white dark:bg-zinc-800 rounded-full p-1.5 shadow-md opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
                                    on:click={(e) => {
                                        const parent = e.currentTarget.parentElement;
                                        if (parent) {
                                            const scrollContainer = parent.querySelector('.overflow-x-auto');
                                            if (scrollContainer instanceof HTMLElement) {
                                                scrollContainer.scrollBy({ left: 300, behavior: 'smooth' });
                                            }
                                        }
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-black/70 dark:text-white/70">
                                        <path d="m9 18 6-6-6-6"/>
                                    </svg>
                                </button>
                            </div>
                        {/if}

                        <!-- 広告セクション -->
                        <div class="mt-4">
                            <ins class="adsbygoogle"
                                style="display:block"
                                data-ad-client="ca-pub-1653216533599284"
                                data-ad-slot="6015068377"
                                data-ad-format="auto"
                                data-full-width-responsive="true"></ins>
                        </div>

                        <!-- 配信予定セクション -->
                        <div class="mt-2">
                            <!-- フィルターヘッダー（スクロールで固定） -->
                            <div 
                                bind:this={upcomingHeaderEl}
                                class="mb-6"
                            >
                                <div class="flex items-center gap-2 mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-black/70 dark:text-white/70">
                                        <circle cx="12" cy="12" r="10"/>
                                        <polyline points="12 6 12 12 16 14"/>
                                    </svg>
                                    <h2 class="text-base font-medium text-black/90 dark:text-white/90">
                                        配信予定の動画
                                    </h2>
                                </div>
                                
                                <!-- フィルター UI -->
                                <div class="flex flex-wrap gap-3 mb-4">
                                    <!-- 日付フィルター -->
                                    <div class="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
                                        <button 
                                            class="px-3 py-1.5 text-xs rounded-full whitespace-nowrap
                                                {selectedDate === '' ? 
                                                    'bg-black dark:bg-white text-white dark:text-black font-medium' : 
                                                    'bg-black/5 dark:bg-white/5 text-black/70 dark:text-white/70 hover:bg-black/10 dark:hover:bg-white/10'}"
                                            on:click={() => selectedDate = ''}
                                        >
                                            すべての日付
                                        </button>
                                        
                                        {#each availableDates as date}
                                            <button 
                                                class="px-3 py-1.5 text-xs rounded-full whitespace-nowrap
                                                    {selectedDate === date ? 
                                                        'bg-black dark:bg-white text-white dark:text-black font-medium' : 
                                                        'bg-black/5 dark:bg-white/5 text-black/70 dark:text-white/70 hover:bg-black/10 dark:hover:bg-white/10'}"
                                                on:click={() => selectedDate = date}
                                            >
                                                {date.replace('年', '/').replace('月', '/').replace('日', '')}
                                            </button>
                                        {/each}
                                    </div>
                                    
                                    <!-- 時間帯フィルター -->
                                    <div class="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
                                        {#each timeRanges as timeRange}
                                            <button 
                                                class="px-3 py-1.5 text-xs rounded-full whitespace-nowrap
                                                    {selectedTimeRange === timeRange.id ? 
                                                        'bg-black dark:bg-white text-white dark:text-black font-medium' : 
                                                        'bg-black/5 dark:bg-white/5 text-black/70 dark:text-white/70 hover:bg-black/10 dark:hover:bg-white/10'}"
                                                on:click={() => selectedTimeRange = timeRange.id}
                                            >
                                                {timeRange.label}
                                            </button>
                                        {/each}
                                    </div>
                                </div>
                            </div>

                            {#if filteredUpcomingChannels.length === 0}
                                <div class="flex flex-col py-6 text-black/60 dark:text-white/60">
                                    <p class="font-medium">現在予定されている配信はありません</p>
                                </div>
                            {:else}
                                {#each sortedTimeRangeChannels as { date, timeGroups }}
                                    <div class="mt-8 first:mt-0">
                                        <!-- 日付 -->
                                        <div class="mb-4">
                                            <time 
                                                datetime={date} 
                                                class="inline-block px-3 py-1 text-sm font-bold bg-black/5 dark:bg-white/10 rounded-md text-black/90 dark:text-white/90"
                                            >
                                                {date}
                                            </time>
                                        </div>
                                        
                                        {#each timeGroups as { timeRange, channels }}
                                            <div class="mt-6 first:mt-0">
                                                <!-- 時間帯 -->
                                                <div class="mb-3">
                                                    <h3 class="text-sm font-medium text-black/70 dark:text-white/70 border-l-2 border-black/20 dark:border-white/20 pl-2">
                                                        {timeRange}
                                                    </h3>
                                                </div>

                                                <!-- 動画グリッド -->
                                                <div 
                                                    class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
                                                    role="list"
                                                    aria-label="{timeRange}の配信予定動画"
                                                >
                                                    {#each channels as channel (channel.id)}
                                                        <div id="channel-{channel.id}" role="listitem" class="relative">
                                                            <div class="absolute top-0 left-0 z-10 px-2 py-1 text-xs font-medium bg-black/80 text-white rounded-br-lg">
                                                                {channel.timeOnly}
                                                            </div>
                                                            <ContentCard 
                                                                id={channel.id || ''}
                                                                thumbnailUrl={channel.thumbnail_url || ''}
                                                                channelTitle={channel.channelName || channel.channel_title || ''}
                                                                title={channel.title || '配信予定'}
                                                                type="upcoming"
                                                                scheduledStartTime={channel.scheduled_start_time}
                                                                channelId={channel.channel_id?.toString()}
                                                            />
                                                        </div>
                                                    {/each}
                                                </div>
                                            </div>
                                        {/each}
                                    </div>
                                {/each}
                            {/if}
                        </div>
                    </div>
                {:else if activeTab === 'archived'}
                    <div 
                        role="tabpanel" 
                        id="archived-content" 
                        aria-labelledby="archived-tab"
                    >
                        <!-- アーカイブ動画表示部分 -->
                        {#if Object.keys(groupVideosByDate(latestVideos)).length === 0}
                            <div class="flex flex-col items-center justify-center py-12 text-gray-600 dark:text-gray-400">
                                <AlertCircle class="w-16 h-16 mb-4 opacity-50" />
                                <p class="text-xl font-semibold">アーカイブされた動画はありません</p>
                                <p class="mt-2">このグループのチャンネルの新しい動画をお待ちください</p>
                            </div>
                        {:else}
                            {#each Object.entries(groupVideosByDate(latestVideos)) as [date, videos]}
                                <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-6 mb-2">{date}</h2>
                                <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                                    {#each videos as video (video.id)}
                                        <ContentCard 
                                            id={video.id}
                                            thumbnailUrl={video.thumbnail_url}
                                            title={video.title}
                                            channelTitle={video.channel_title}
                                            type="archived"
                                            duration={video.duration}
                                            channelId={video.channel_id}
                                            publishedAt={video.published_at}
                                            liveStats={video.live_stats}
                                            />
                                    {/each}
                                </div>
                            {/each}
                        {/if}
                    </div>
                {/if}
            {/if}
        </div>
    {/if}
</div>

<style>
    .tab-button {
        position: relative;
        padding: 0.5rem 1rem;
        font-weight: 500;
        font-size: 0.875rem;
        color: #666666;
        border-bottom: 2px solid transparent;
        transition: all 0.3s ease;
    }

    .tab-button.active {
        color: #000000;
        border-color: #000000;
    }

    :global(.dark) .tab-button {
        color: #999999;
    }

    :global(.dark) .tab-button.active {
        color: #ffffff;
        border-color: #ffffff;
    }

    .tab-button:hover {
        color: #333333;
    }

    :global(.dark) .tab-button:hover {
        color: #cccccc;
    }

    .tab-button::after {
        content: '';
        position: absolute;
        left: 50%;
        bottom: -2px;
        width: 0;
        height: 2px;
        background: #000000;
        transition: width 0.3s ease, left 0.3s ease;
    }

    :global(.dark) .tab-button::after {
        background: #ffffff;
    }

    .tab-button.active::after {
        width: 100%;
        left: 0;
    }

    .time-range {
        margin: 1rem 0;
        padding: 0.2rem 0.4rem;
        background: rgba(0, 0, 0, 0.8);
        border-radius: 0.5rem;
        color: #fff;
        font-weight: bold;
        display: inline-block;
    }

    :global(.dark) .time-range {
        background: rgba(255, 255, 255, 0.1);
    }

    /* アニメーションの追加 */
    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
    }

    .animate-pulse {
        animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    /* スクロールバーを非表示にするためのスタイル */
    .no-scrollbar {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }
    .no-scrollbar::-webkit-scrollbar {
        display: none;
    }

    /* 左右のフェードエッジ */
    .mask-edges {
        mask-image: linear-gradient(to right, transparent, black 2rem, black calc(100% - 2rem), transparent);
        -webkit-mask-image: linear-gradient(to right, transparent, black 2rem, black calc(100% - 2rem), transparent);
    }
</style>
